import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SportsRugby from "@mui/icons-material/SportsRugby";
import PeopleIcon from "@mui/icons-material/People";
import { Button, Grid, Link, Stack, Typography, Box, Paper } from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { WARM_UP_MESSAGE } from "../App";
import { CustomSpinner } from "../components/CustomSpinner";
import { FeatureCard } from "../components/FeatureCard";
import { PageLayout } from "../components/PageLayout";
import { useUser, UserPermission } from "../requests/users";
import { borderRadius } from "../utils/designSystem";

export const TRACKER_DESCRIPTION = "Track match events in real-time with our intuitive interface.";
export const VIDEO_REVIEW_DESCRIPTION =
  "Analyze match footage with advanced filtering and tagging capabilities.";
export const ANALYTICS_DESCRIPTION =
  "Gain insights from your match data with comprehensive analytics.";
export const TEAMS_DESCRIPTION = "Manage your database of opponents.";
export const USERS_DESCRIPTION = "Manage users and invitations for your organization.";
const LEAGUE_BLOCKED_ORGANIZATION_IDS = [374 /* Casey Warriors */];

export const Home = () => {
  const user = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.organization_id === undefined) {
      navigate("/new-user");
    }
  }, [user, navigate]);

  if (!user || user.organization_id === undefined) {
    return <CustomSpinner message={WARM_UP_MESSAGE} />;
  }

  return (
    <PageLayout title="Welcome to QuickTap" subtitle="" maxWidth="lg">
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <FeatureCard
            title="Tracker"
            description={TRACKER_DESCRIPTION}
            icon={<SportsRugby sx={{ fontSize: "40px" }} />}
            onClick={() => navigate("/matches")}
            minUserPermission={UserPermission.track}
            leagueBlockedOrganizationIds={LEAGUE_BLOCKED_ORGANIZATION_IDS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FeatureCard
            title="Video Review"
            description={VIDEO_REVIEW_DESCRIPTION}
            icon={<OndemandVideoIcon sx={{ fontSize: "40px" }} />}
            onClick={() => navigate("/video-review")}
            isLocked={!user.organization_permissions?.video_review_enabled}
            lockMessage="Video Review requires a QuickTap Pro account."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FeatureCard
            title="Analytics"
            description={ANALYTICS_DESCRIPTION}
            icon={<InsightsIcon sx={{ fontSize: "40px" }} />}
            onClick={() => navigate("/analytics")}
            isLocked={!user.organization_permissions?.video_review_enabled}
            lockMessage="Analytics requires a QuickTap Pro account."
            leagueBlockedOrganizationIds={LEAGUE_BLOCKED_ORGANIZATION_IDS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FeatureCard
            title="Teams"
            description={TEAMS_DESCRIPTION}
            icon={<GroupsIcon sx={{ fontSize: "40px" }} />}
            onClick={() => navigate("/teams")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FeatureCard
            title="Users"
            description={USERS_DESCRIPTION}
            icon={<PeopleIcon sx={{ fontSize: "40px" }} />}
            onClick={() => navigate("/users")}
            minUserPermission={UserPermission.owner}
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: borderRadius.lg,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack
          spacing={3}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Quick Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your account and access documentation
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Link href="/account" style={{ textDecoration: "none" }}>
              <Button variant="outlined" sx={{ borderRadius: borderRadius.md }}>
                Manage Account
              </Button>
            </Link>
            <Link
              href="https://getquicktap.com/docs"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Button variant="outlined" sx={{ borderRadius: borderRadius.md }}>
                Documentation
              </Button>
            </Link>
            <Button
              variant="outlined"
              sx={{ borderRadius: borderRadius.md }}
              onClick={() => window.open("mailto:support@getquicktap.com", "_blank")}
            >
              Contact Support
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </PageLayout>
  );
};
